import { formatCurrency } from '@/app/utils/formatCurrency';
import { formatIsoDateOnlyShort } from '@/app/utils/formatDate';
import type { ConfiguredAddOn, ConfiguredService } from '@/features/service-plans';
import type { PricedBasket, PricedLineFigures } from '../types/basketPricing.types';
import { pricedServiceLine, type MergedPricedLines } from './pricedLines';

const PLACEHOLDER = '—';

export interface QuoteSummaryTotalsView {
    readonly subtotal: string;
    readonly vat: string;
    readonly total: string;
}

export interface QuoteLineView {
    readonly id: string;
    readonly name: string;
    readonly description: string;
    readonly net: string;
    readonly gross: string;
}

export interface QuoteServiceLineView extends QuoteLineView {
    readonly dueDate: string;
    readonly items: ConfiguredService['items'];
    readonly costMode: ConfiguredService['costMode'];
    readonly pricedLine: PricedLineFigures | undefined;
}

const isPricedOrAsSold = (
    instanceId: string | number,
    merged: MergedPricedLines,
    pricing: PricedBasket | undefined,
): boolean => pricing === undefined || merged.pricedInstanceIds.has(instanceId);

const lineFigure = (shown: boolean, amount: number, currency: string): string =>
    shown ? formatCurrency(amount, currency) : PLACEHOLDER;

const buildLineView = (
    item: ConfiguredService | ConfiguredAddOn,
    merged: MergedPricedLines,
    pricing: PricedBasket | undefined,
    currency: string,
): QuoteLineView => {
    const shown = isPricedOrAsSold(item.instanceId, merged, pricing);
    return {
        id: String(item.instanceId),
        name: item.name,
        description: item.description || PLACEHOLDER,
        net: lineFigure(shown, item.price, currency),
        gross: lineFigure(shown, item.gross_price, currency),
    };
};

export const buildServiceLineViews = (
    merged: MergedPricedLines,
    pricing: PricedBasket | undefined,
    currency: string,
): QuoteServiceLineView[] =>
    merged.services.map((service) => ({
        ...buildLineView(service, merged, pricing, currency),
        dueDate: formatIsoDateOnlyShort(service.startDate) || PLACEHOLDER,
        items: service.items,
        costMode: service.costMode,
        pricedLine: pricedServiceLine(pricing, service.instanceId),
    }));

export const buildAddOnLineViews = (
    merged: MergedPricedLines,
    pricing: PricedBasket | undefined,
    currency: string,
): QuoteLineView[] => merged.addOns.map((addOn) => buildLineView(addOn, merged, pricing, currency));

export const buildTotalsView = (
    summary: { subTotal: number; vat: number; total: number } | null,
    currency: string,
): QuoteSummaryTotalsView => {
    if (summary === null) {
        return { subtotal: PLACEHOLDER, vat: PLACEHOLDER, total: PLACEHOLDER };
    }
    return {
        subtotal: formatCurrency(summary.subTotal, currency),
        vat: formatCurrency(summary.vat, currency),
        total: formatCurrency(summary.total, currency),
    };
};
